<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Historial de vacaciones</h1>
    </div>

    <wz-table-container>

      <wz-table-toolbar>
        <template #left>
          <wz-filter-menu
            :options="filterOptions"
            :selected-option-filter="filters"
            @setFilters="setFilters"
          />
        </template>

        <template #right>
          <wz-tooltip text="Recargar">
            <wz-button variant="outline" @click="loadData()">
              <i class="ri-loop-right-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-tooltip text="Descargar Excel">
            <wz-button variant="outline" @click="downloadReport()">
              <i class="ri-download-2-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-search-bar
            placeholder="Buscar por nombre o número de documento"
            @search="onSearch"
            @input="search = $event"
          />
        </template>

        <template #filters v-if="showFiltersList">
          <wz-badge
            v-for="(val, name) in filterList"
            :key="name"
            variant="primary"
            pill
            dismissible
            @dismiss="deleteFilter(name)"
          >
            {{ getFilterName(name, val) }}
          </wz-badge>
        </template>
      </wz-table-toolbar>

      <wz-table>
        <thead>
          <tr>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'employeeName' }" @click="toggleSort('employeeName')">
              Colaborador <i :class="['wz-th-sort-icon', sortIcon('employeeName')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'companyName' }" @click="toggleSort('companyName')">
              Empresa <i :class="['wz-th-sort-icon', sortIcon('companyName')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'areaName' }" @click="toggleSort('areaName')">
              Área <i :class="['wz-th-sort-icon', sortIcon('areaName')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'startDate' }" @click="toggleSort('startDate')">
              Fecha Inicio <i :class="['wz-th-sort-icon', sortIcon('startDate')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'endDate' }" @click="toggleSort('endDate')">
              Fecha Fin <i :class="['wz-th-sort-icon', sortIcon('endDate')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'calendarDays' }" @click="toggleSort('calendarDays')">
              Días <i :class="['wz-th-sort-icon', sortIcon('calendarDays')]"></i>
            </th>
            <th>Revisado por</th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'status' }" @click="toggleSort('status')">
              Estado <i :class="['wz-th-sort-icon', sortIcon('status')]"></i>
            </th>
            <th>Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="paginatedList.length === 0">
            <td colspan="9" class="text-center text-muted py-4">No se encontraron registros</td>
          </tr>
          <tr v-for="item in paginatedList" :key="item.id">
            <td>{{ item.employeeName || '-' }}</td>
            <td>{{ item.companyName || '-' }}</td>
            <td>{{ item.areaName || '-' }}</td>
            <td v-local-date="item.startDate"></td>
            <td v-local-date="item.endDate"></td>
            <td>{{ item.calendarDays || '-' }}</td>
            <td>{{ getReviewedBy(item) }}</td>
            <td>
              <wz-badge v-if="item.requestType === 'HISTORICAL'" variant="secondary" pill>
                HISTÓRICO
              </wz-badge>
              <wz-badge v-else :variant="statusVariant(item.status)" pill>
                {{ item.status }}
              </wz-badge>
            </td>
            <td style="text-align: center;">
              <wz-dropdown
                :items="[{ key: 'view', label: 'Ver detalle' }]"
                placement="left"
                @click="$router.push({ name: 'vacation-requests-detail', params: { id: item.id } })"
              />
            </td>
          </tr>
        </tbody>
      </wz-table>

      <wz-table-footer>
        <wz-pagination
          v-if="list.length > 0"
          :total="list.length"
          :current-page="page"
          :per-page="pageSize"
          :per-page-options="[10, 20, 50, 100]"
          @change="onPaginationChange"
        />
        <div v-else class="table-no-results">
          <p class="m-0 px-3">No se encontraron registros.</p>
        </div>
      </wz-table-footer>

    </wz-table-container>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { FilterMixin } from "@wizepe/wizeui"
import { SortMixin } from '@wizepe/wizeui'
import { VacationService, CompanyService, AreaService } from '@/core/services'
import { Constants as LocalConstants } from '@/core/utils'

export default {
  name: 'VacationHistory',
  mixins: [SortMixin, FilterMixin],
  data() {
    return {
      list:     [],
      search:   '',
      page:     1,
      pageSize: 100,
      filterOptions: [
        {
          label: 'Estado',
          key: 'status',
          type: 'SINGLE',
          options: [
            ...Object.values(LocalConstants.VacationRequestStatus).map(s => ({ key: s.key, label: s.label })),
            { key: 'HISTORICAL', label: 'Histórico' },
          ],
        },
        {
          label: 'Empresa',
          key: 'companyId',
          type: 'SINGLE',
          options: [],
        },
        {
          label: 'Área',
          key: 'areaId',
          type: 'SINGLE',
          options: [],
        },
        {
          label: 'Fecha de inicio',
          key: 'startDate',
          type: 'DATE',
          isRange: true,
        },
      ],
    }
  },
  computed: {
    paginatedList() {
      const sorted = this.sortedList(this.list)
      const start = (this.page - 1) * this.pageSize
      return sorted.slice(start, start + this.pageSize)
    },
  },
  methods: {
    statusVariant(status) {
      const map = {
        PENDIENTE: 'warning-solid',
        APROBADA:  'success-solid',
        RECHAZADA: 'danger-solid',
        CANCELADA: 'dark',
      }
      return map[status] || 'dark'
    },
    getReviewedBy(item) {
      if (item.status === 'APROBADA')  return item.approvedBy  || '-'
      if (item.status === 'CANCELADA') return item.cancelledBy || '-'
      return '-'
    },
    onPaginationChange({ page, perPage }) {
      this.page = page
      this.pageSize = perPage
    },
    onSearch(val) {
      this.search = val
      this.loadData()
    },
    buildFilterParams() {
      const params = {}
      if (this.filters.status) {
        if (this.filters.status === 'HISTORICAL') {
          params.requestType = 'HISTORICAL'
        } else {
          params.status = this.filters.status
        }
      }
      if (this.filters.companyId) params.companyId = this.filters.companyId
      if (this.filters.areaId)    params.areaId    = this.filters.areaId
      if (this.filters.startDate?.start) params.startDate = this.filters.startDate.start
      if (this.filters.startDate?.end)   params.endDate   = this.filters.startDate.end
      if (this.search) params.search = this.search
      return params
    },
    async loadData() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await VacationService.getRequests(this.buildFilterParams())
        this.list = resp.payload || []
        this.page = 1
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar el historial')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async downloadReport() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await VacationService.downloadXlsx(this.buildFilterParams())
        const today = new Date().toISOString().split('T')[0]
        const url  = window.URL.createObjectURL(resp.data)
        const link = document.createElement('a')
        link.href  = url
        link.setAttribute('download', `historial-vacaciones-${today}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        Alert.error('Error al descargar el reporte')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() {
    try {
      const [companiesResp, areasResp] = await Promise.all([
        CompanyService.listAll(),
        AreaService.listAll(),
      ])
      this.filterOptions[1].options = (companiesResp.payload || []).map(c => ({ key: c.id, label: c.businessName || c.name }))
      this.filterOptions[2].options = (areasResp.payload || []).map(a => ({ key: a.id, label: a.description }))
    } catch (e) {
      console.error(e)
    }
    this.loadData()
  },
}
</script>

<style lang="stylus"></style>
